import React, { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { supabase } from '../supabaseClient';
import type { User } from '@supabase/supabase-js';
import type { AppData, Profile, Mission, Clinic, Subscription } from '../types';
import { getErrorMessage } from '../utils/errors';
import { userKey } from '../lib/deviceStore';

const AppDataContext = createContext<AppData | null>(null);

const POLL_INTERVAL_MS = 30_000; // 30 seconds - was 5s in useAppData, 120s in context

export const AppDataProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [clinic, setClinic] = useState<Clinic | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const [debugStatus, setDebugStatus] = useState('Initializing...');

  // Refs to avoid stale closures in interval callbacks
  const profileRef = useRef<Profile | null>(null);
  const userRef = useRef<User | null>(null);
  profileRef.current = profile;
  userRef.current = user;

  // ─── Streak ──────────────────────────────────────────────────────────────

  const checkStreak = async (currentProfile: Profile) => {
    if (!currentProfile.last_seen) return;

    const lastSeenMidnight = new Date(currentProfile.last_seen).setHours(0, 0, 0, 0);
    const todayMidnight = new Date().setHours(0, 0, 0, 0);
    const diffDays = Math.round((todayMidnight - lastSeenMidnight) / 86_400_000);

    let newStreak = currentProfile.streak;
    if (diffDays === 1) newStreak += 1;
    else if (diffDays > 1) newStreak = 1;

    const updates = {
      last_seen: new Date().toISOString(),
      streak: diffDays >= 1 ? newStreak : currentProfile.streak,
    };

    await supabase.from('profiles').update(updates).eq('id', currentProfile.id);
    setProfile((prev: Profile | null) => (prev ? { ...prev, ...updates } : null));
  };

  // ─── Welcome missions ─────────────────────────────────────────────────────

  const assignWelcomeMissions = async (userId: string) => {
    const welcomeMissions = [
      {
        user_id: userId,
        title: 'סלפי ראשון למסע',
        description: 'צלם תמונה שבה אתה מחייך לעולם.',
        xp_reward: 50,
        type: 'active',
        rationale: 'חיזוק הדימוי העצמי.',
        is_completed: false,
      },
      {
        user_id: userId,
        title: 'נשימה מודעת',
        description: 'קח דקה של שקט עם משחק הנשימות.',
        xp_reward: 30,
        type: 'mindful',
        rationale: 'לימוד טכניקת הרגעה.',
        is_completed: false,
      },
      {
        user_id: userId,
        title: 'משהו טוב בך',
        description: 'כתוב תכונה אחת שאתה אוהב בעצמך.',
        xp_reward: 40,
        type: 'creative',
        rationale: 'חיזוק הערך העצמי.',
        is_completed: false,
      },
    ];

    const { data, error } = await supabase.from('missions').insert(welcomeMissions).select();
    if (!error && data) setMissions(data as Mission[]);
  };

  // ─── Fetch ────────────────────────────────────────────────────────────────

  const fetchData = async (showSpinner = false) => {
    try {
      if (showSpinner) setLoading(true);
      setDebugStatus('Checking session...');

      const sessionResult = await Promise.race([
        supabase.auth.getSession(),
        new Promise<{ data: { session: null }; error: Error }>((resolve) =>
          setTimeout(
            () => resolve({ data: { session: null }, error: new Error('Session timeout') }),
            5000,
          ),
        ),
      ]);

      const { data: { session }, error: sessionError } = sessionResult;

      if (sessionError || !session?.user) {
        setDebugStatus('No session');
        setUser(null);
        setProfile(null);
        setClinic(null);
        setSubscription(null);
        setMissions([]);
        return;
      }

      setUser(session.user);
      setDebugStatus('Fetching data...');

      const [profileRes, missionsRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', session.user.id).single(),
        supabase
          .from('missions')
          .select('*')
          .eq('user_id', session.user.id)
          .order('is_completed', { ascending: true })
          .order('id', { ascending: false }),
      ]);

      if (profileRes.data) {
        // Sync local avatar override
        const localAvatar = localStorage.getItem('remind_avatar');
        if (localAvatar && localAvatar !== profileRes.data.avatar_id) {
          supabase
            .from('profiles')
            .update({ avatar_id: localAvatar })
            .eq('id', session.user.id)
            .then(({ error }: { error: Error | null }) => { if (error) console.error('Avatar sync failed:', error); });
        }
        if (localAvatar) profileRes.data.avatar_id = localAvatar;

        // Self-heal: balance > xp means data desync
        if ((profileRes.data.balance ?? 0) > (profileRes.data.xp ?? 0)) {
          profileRes.data.xp = profileRes.data.balance;
          await supabase
            .from('profiles')
            .update({ xp: profileRes.data.balance })
            .eq('id', session.user.id);
        }

        setProfile(profileRes.data);

        if (profileRes.data.role === 'patient') {
          await checkStreak(profileRes.data);
        } else if (profileRes.data.role === 'therapist' && profileRes.data.clinic_id) {
          // Fetch clinic and subscription for therapist
          const [clinicRes, subRes] = await Promise.all([
            supabase.from('clinics').select('*').eq('id', profileRes.data.clinic_id).single(),
            supabase.from('subscriptions').select('*').eq('clinic_id', profileRes.data.clinic_id).order('created_at', { ascending: false }).limit(1).single()
          ]);
          if (clinicRes.data) setClinic(clinicRes.data);
          if (subRes.data) setSubscription(subRes.data);
        }
      }

      if (missionsRes.data) {
        // Daily reset
        const lastReset = localStorage.getItem('last_mission_reset');
        const today = new Date().toLocaleDateString('he-IL');
        if (lastReset !== today) {
          await supabase
            .from('missions')
            .update({ is_completed: false })
            .eq('user_id', session.user.id);
          setMissions(missionsRes.data.map((m: Mission) => ({ ...m, is_completed: false })));
          localStorage.setItem('last_mission_reset', today);
        } else {
          setMissions(missionsRes.data);
        }

        if (missionsRes.data.length === 0 && profileRes.data?.role === 'patient') {
          await assignWelcomeMissions(session.user.id);
        }
      }

      setDebugStatus('Ready');
    } catch (err) {
      console.error('fetchData error:', err);
      setDebugStatus(`Error: ${getErrorMessage(err)}`);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  // ─── Actions ──────────────────────────────────────────────────────────────

  const completeMission = async (missionId: number | string, xpReward: number) => {
    const currentProfile = profileRef.current;
    if (!currentProfile) return;

    const currentXP = currentProfile.xp ?? 0;
    const currentBalance = currentProfile.balance ?? 0;
    const currentLevel = currentProfile.level ?? 1;

    const newXP = currentXP + xpReward;
    const newLevel = Math.floor(newXP / 1000) + 1;
    const isLevelUp = newLevel > currentLevel;
    const levelUpBonus = isLevelUp ? 100 : 0;
    const newBalance = currentBalance + xpReward + levelUpBonus;

    // Save to local history
    const completedMission = missions.find((m: Mission) => m.id === missionId);
    if (completedMission) {
      const historyKey = userKey('remind_mission_history', currentProfile.id);
      const history = JSON.parse(localStorage.getItem(historyKey) ?? '[]');
      history.unshift({ id: Date.now(), title: completedMission.title, xp: xpReward, date: new Date().toISOString() });
      localStorage.setItem(historyKey, JSON.stringify(history));
    }

    // Optimistic update
    setMissions((prev: Mission[]) => prev.map((m: Mission) => (m.id === missionId ? { ...m, is_completed: true } : m)));
    setProfile((prev: Profile | null) => prev ? { ...prev, xp: newXP, level: newLevel, balance: newBalance } : null);
    if (isLevelUp) setShowLevelUp(true);

    // Persist
    await supabase.from('missions').update({ is_completed: true }).eq('id', missionId);
    await supabase
      .from('profiles')
      .update({ xp: newXP, balance: newBalance, ...(isLevelUp && { level: newLevel }) })
      .eq('id', currentProfile.id);

    await supabase.from('xp_history').insert({ user_id: currentProfile.id, xp_earned: xpReward });
  };

  const buyItem = async (itemId: string, cost: number): Promise<boolean> => {
    const currentProfile = profileRef.current;
    if (!currentProfile) return false;

    if ((currentProfile.balance ?? 0) < cost) {
      alert('אין לך מספיק מטבעות!');
      return false;
    }
    if ((currentProfile.owned_items ?? []).includes(itemId)) {
      alert('כבר יש לך את הפריט הזה!');
      return false;
    }

    const newBalance = (currentProfile.balance ?? 0) - cost;
    const newOwned = [...(currentProfile.owned_items ?? []), itemId];

    setProfile((prev: Profile | null) => prev ? { ...prev, balance: newBalance, owned_items: newOwned } : null);

    const { error } = await supabase
      .from('profiles')
      .update({ balance: newBalance, owned_items: newOwned })
      .eq('id', currentProfile.id);

    return !error;
  };

  const updateUsername = async (newUsername: string): Promise<{ success: boolean; message?: string }> => {
    const currentProfile = profileRef.current;
    if (!currentProfile) return { success: false, message: 'No profile' };

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ username: newUsername })
        .eq('id', currentProfile.id);
      if (error) throw error;
      setProfile((prev: Profile | null) => prev ? { ...prev, username: newUsername } : null);
      return { success: true };
    } catch (err) {
      return { success: false, message: getErrorMessage(err) };
    }
  };

  const updateFullName = async (newName: string): Promise<{ success: boolean; message?: string }> => {
    const currentProfile = profileRef.current;
    if (!currentProfile) return { success: false, message: 'No profile' };

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ full_name: newName })
        .eq('id', currentProfile.id);
      if (error) throw error;
      setProfile((prev: Profile | null) => prev ? { ...prev, full_name: newName } : null);
      return { success: true };
    } catch (err) {
      return { success: false, message: getErrorMessage(err) };
    }
  };

  const updateAvatar = async (avatarId: string): Promise<void> => {
    localStorage.setItem('remind_avatar', avatarId);
    setProfile((prev: Profile | null) => prev ? { ...prev, avatar_id: avatarId } : null);
    const currentProfile = profileRef.current;
    if (!currentProfile) return;
    await supabase.from('profiles').update({ avatar_id: avatarId }).eq('id', currentProfile.id);
  };

  const addDiamonds = async (amount: number): Promise<void> => {
    const currentProfile = profileRef.current;
    if (!currentProfile) return;

    const newBalance = (currentProfile.balance ?? 0) + amount;
    setProfile((prev: Profile | null) => prev ? { ...prev, balance: newBalance } : null);

    const { error } = await supabase
      .from('profiles')
      .update({ balance: newBalance })
      .eq('id', currentProfile.id);

    if (error) console.error('addDiamonds failed:', error);
  };

  // ─── Polling with Page Visibility API ────────────────────────────────────

  useEffect(() => {
    fetchData(true);

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startPolling = () => {
      if (intervalId) return;
      intervalId = setInterval(() => fetchData(false), POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    // Only poll when tab is visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchData(false); // Immediate refresh when returning to tab
        startPolling();
      } else {
        stopPolling();
      }
    };

    startPolling();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const { data: authListener } = supabase.auth.onAuthStateChange(() => {
      fetchData(true);
    });

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      authListener.subscription.unsubscribe();
    };
    // Subscribe once on mount; fetchData reads fresh state from Supabase on every call.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Context value ────────────────────────────────────────────────────────

  return (
    <AppDataContext.Provider
      value={{
        user,
        profile,
        clinic,
        subscription,
        missions,
        loading,
        showLevelUp,
        debugStatus,
        closeLevelUp: () => setShowLevelUp(false),
        completeMission,
        buyItem,
        updateUsername,
        updateFullName,
        updateAvatar,
        addDiamonds,
      }}
    >
      {children}
    </AppDataContext.Provider>
  );
};

// ─── Hooks ────────────────────────────────────────────────────────────────────
// Kept next to the provider on purpose; editing this file triggers a full reload instead of Fast Refresh.

// eslint-disable-next-line react-refresh/only-export-components
export const useAppDataContext = (): AppData => {
  const context = useContext(AppDataContext);
  if (!context) throw new Error('useAppDataContext must be used within AppDataProvider');
  return context;
};

/**
 * Backward-compatible alias - replaces the old useAppData hook.
 * Components can migrate to useAppDataContext over time.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAppData = useAppDataContext;